// Spaces are channels with type='space'. The export name
// `clientSpacesQueries` is preserved as an alias for one PR cycle so
// existing callers keep working; new code should import `spacesQueries`
// directly. Cosmetic alias removal happens in a follow-up PR.
//
// `clientSpaceMembersQueries` is a shim that reads space memberships
// from `channel_members` (where the underlying channel has type='space').
// Used by InviteDialog.tsx and similar callers; remove once consumers
// migrate to channel_members directly.

import { defineQuery } from "@rocicorp/zero";
import { z } from "zod";
import type {} from "../zero-context";
import { zql } from "../schema";
import { ADMIN_PLUS } from "../constants";
import { workspaceFullMemberGuard } from "./access-filters";

export const spacesQueries = {
  // NOTE: `byWorkspace` and `byWorkspaceWithMembers` were removed (zero
  // callers) — they gated on `workspaceFullMemberGuard` (any full member)
  // instead of space membership and never filtered `is_private`, leaking
  // private-space rows + member rosters to non-members. Space rows are read
  // via the membership-scoped `byId` / `byUserMembership` /
  // `byWorkspaceWithChannels` queries; public-space discovery flows through
  // `publicByWorkspaceForBrowse` (excludes `is_private=true`); admin views
  // use `byWorkspaceForAdmin` (ADMIN_PLUS-gated). Do not re-add a
  // full-member-gated list query without an `is_private` / membership filter.

  // Sidebar: only spaces the user is a member of (channel_members row
  // on the space). Loads child channels + workspace links for sidebar
  // display.
  byWorkspaceWithChannels: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.channels
        .where("type", "space")
        .where("workspace_id", workspaceId)
        .whereExists("members", (mq) =>
          mq.where("user_id", sub).where("removed_at", "IS", null),
        )
        .related("child_channels")
        .related("workspace_links"),
  ),

  // Visibility: caller must be a member of the space OR a workspace+
  // admin. Tightened in PR #1250 round 4 — was `workspaceFullMemberGuard`
  // which let any full member (including non-members of a private space)
  // read the row. Public-space discovery now flows through
  // `publicByWorkspaceForBrowse` which excludes `is_private=true`.
  byId: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.channels
      .where("type", "space")
      .where("id", id)
      .where(({ exists, or }) =>
        or(
          // Direct space membership
          exists("members", (mq) =>
            mq.where("user_id", sub).where("removed_at", "IS", null),
          ),
          // Workspace admin+ — same path the admin queries use.
          exists("workspace", (wq) =>
            wq.whereExists("members", (mq) =>
              mq
                .where("user_id", sub)
                .where("role", "IN", ADMIN_PLUS as readonly string[])
                .where("removed_at", "IS", null),
            ),
          ),
        ),
      )
      .one(),
  ),

  byIdWithRelated: defineQuery(z.string(), ({ args: id, ctx: { sub } }) =>
    zql.channels
      .where("type", "space")
      .where("id", id)
      .where(({ exists, or }) =>
        or(
          exists("members", (mq) =>
            mq.where("user_id", sub).where("removed_at", "IS", null),
          ),
          exists("workspace", (wq) =>
            wq.whereExists("members", (mq) =>
              mq
                .where("user_id", sub)
                .where("role", "IN", ADMIN_PLUS as readonly string[])
                .where("removed_at", "IS", null),
            ),
          ),
        ),
      )
      .related("members", (q) =>
        q.where("removed_at", "IS", null).related("user"),
      )
      .related("child_channels")
      .related("creator")
      .related("invite_links")
      .one(),
  ),

  // Admin view: all spaces in workspace (public + private, active +
  // archived), regardless of membership. Used by admin management UI.
  // Gated on ADMIN_PLUS.
  byWorkspaceForAdmin: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.channels
        .where("type", "space")
        .where("workspace_id", workspaceId)
        .whereExists("workspace", (wq) =>
          wq.whereExists("members", (mq) =>
            mq
              .where("user_id", sub)
              .where("role", "IN", ADMIN_PLUS as readonly string[])
              .where("removed_at", "IS", null),
          ),
        )
        .related("members", (q) => q.where("removed_at", "IS", null))
        .related("child_channels", (q) =>
          q.related("members", (mq) => mq.where("removed_at", "IS", null)),
        ),
  ),

  // Spaces linked to a workspace (role='linked') via workspace_links.
  // For sidebar: shows external spaces that have been connected to this
  // workspace. Requires user to be both a space member AND a member of
  // the linked workspace.
  byLinkedWorkspace: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.channels
        .where("type", "space")
        .whereExists("workspace_links", (wl) =>
          wl
            .where("workspace_id", workspaceId)
            .where("role", "linked")
            .whereExists("workspace", (wsq) =>
              wsq.whereExists("members", (mq) =>
                mq.where("user_id", sub).where("removed_at", "IS", null),
              ),
            ),
        )
        .whereExists("members", (mq) =>
          mq.where("user_id", sub).where("removed_at", "IS", null),
        )
        .related("child_channels")
        .related("workspace_links"),
  ),

  // Spaces linked to a workspace with full member/channel details.
  byLinkedWorkspaceWithMembers: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.channels
        .where("type", "space")
        .whereExists("workspace_links", (wl) =>
          wl
            .where("workspace_id", workspaceId)
            .where("role", "linked")
            .whereExists("workspace", (wsq) =>
              wsq.whereExists("members", (mq) =>
                mq.where("user_id", sub).where("removed_at", "IS", null),
              ),
            ),
        )
        .whereExists("members", (mq) =>
          mq.where("user_id", sub).where("removed_at", "IS", null),
        )
        .related("members", (q) =>
          q.where("removed_at", "IS", null).related("user"),
        )
        .related("child_channels")
        .related("workspace_links")
        .related("workspace"),
  ),

  // Spaces the current user belongs to in a workspace (works for
  // collaborators). Returns spaces with their child channels +
  // workspace links for sidebar grouping.
  byUserMembership: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.channels
        .where("type", "space")
        .where("workspace_id", workspaceId)
        .whereExists("members", (mq) =>
          mq.where("user_id", sub).where("removed_at", "IS", null),
        )
        .related("child_channels")
        .related("workspace_links"),
  ),

  // Browse: all public spaces in workspace, regardless of membership.
  // Used by BrowseChannelsDialog for space discovery + joining.
  // Private spaces excluded server-side. Auth: full workspace member.
  publicByWorkspaceForBrowse: defineQuery(
    z.string(),
    ({ args: workspaceId, ctx: { sub } }) =>
      zql.channels
        .where("type", "space")
        .where("workspace_id", workspaceId)
        .where("is_private", false)
        .where("is_archived", false)
        .whereExists("workspace", workspaceFullMemberGuard(sub))
        .related("members", (q) => q.where("removed_at", "IS", null)),
  ),
};

// Backwards-compat alias for one PR cycle. Delete in a follow-up
// cosmetic PR alongside the queries map's `client_spaces` key removal.
export const clientSpacesQueries = spacesQueries;

export const clientSpaceMembersQueries = {
  bySpace: defineQuery(z.string(), ({ args: spaceId, ctx: { sub } }) =>
    zql.channel_members
      .where("channel_id", spaceId)
      .where("removed_at", "IS", null)
      .whereExists("channel", (cq) =>
        cq
          .where("type", "space")
          .whereExists("workspace", workspaceFullMemberGuard(sub)),
      ),
  ),

  bySpaceWithUsers: defineQuery(z.string(), ({ args: spaceId, ctx: { sub } }) =>
    zql.channel_members
      .where("channel_id", spaceId)
      .where("removed_at", "IS", null)
      .whereExists("channel", (cq) =>
        cq
          .where("type", "space")
          .whereExists("workspace", workspaceFullMemberGuard(sub)),
      )
      .related("user"),
  ),
};

export const clientSpaceInviteLinksQueries = {
  // Permission SELECT restricts to admin+ only — align query to match
  bySpace: defineQuery(z.string(), ({ args: spaceId, ctx: { sub } }) =>
    zql.invites
      .where("client_space_id", spaceId)
      .where("type", "client_space")
      .whereExists("parent_channel", (cq) =>
        cq.where("type", "space").whereExists("workspace", (wq) =>
          wq.whereExists("members", (mq) =>
            mq
              .where("user_id", sub)
              .where("role", "IN", ADMIN_PLUS as readonly string[])
              .where("removed_at", "IS", null),
          ),
        ),
      ),
  ),
};
